import { useEffect, useState, useRef, useCallback } from "react";
import { boxesIntersect } from "@air/react-drag-to-select";
import { OUTLINE_CARD_TYPE, TEXT_CARD_TYPE } from "../../../../../src/shared/canvasConstants";
import { SET_DATA_ACTION, SET_DATA_SILENT_ACTION } from "./useCanvasReducer";

/**
 * Allows dragging and dropping text cards onto outline cards
 */

export default ({
  cards, //
  dispatch,
  cardIdsString,
  flushSelectionCache,
}) => {
  const [outlineCards, setOutlineCards] = useState([]);
  const [intersectingCard, setIntersectingCard] = useState(null);
  const prevDeltas = useRef(null);
  const timeoutId = useRef(null);

  const calculateOutlineCards = useCallback(() => {
    setOutlineCards(
      Object.keys(cards)
        .map((id) => {
          if (cards[id].type === OUTLINE_CARD_TYPE) {
            return cards[id];
          } else {
            return null;
          }
        })
        .filter((c) => c !== null)
    );
  }, [cards]);

  useEffect(() => {
    if (cards) {
      calculateOutlineCards();
    }
  }, [cardIdsString]);

  const onDragEnd = useCallback(
    (id) => {
      // if dragging is stopped over the outline card, SWAP it!
      if (intersectingCard) {
        console.log("intersecting", intersectingCard);
        dispatch({ type: SET_DATA_ACTION, id: intersectingCard.id, data: { content: cards[id].content } });
        dispatch({ type: SET_DATA_ACTION, id, data: { content: cards[intersectingCard.id].content } });
      }

      // clear intersection
      setIntersectingCard(null);
      calculateOutlineCards();
      flushSelectionCache();
      clearTimeout(timeoutId.current);

      prevDeltas.current = null;
    },
    [cards]
  );

  const calculateIntersection = useCallback(
    (id) => {
      if (cards[id].type === TEXT_CARD_TYPE) {
        // calculate intersection
        const draggingCard = cards[id];

        const draggingCardBox = {
          top: draggingCard.y,
          left: draggingCard.x,
          width: draggingCard.width,
          height: draggingCard.height,
        };

        let found = false;

        for (const outlineCard of outlineCards) {
          if (!found) {
            const cardBox = {
              top: outlineCard.y,
              left: outlineCard.x,
              width: outlineCard.width,
              height: outlineCard.height,
            };

            if (boxesIntersect(draggingCardBox, cardBox)) {
              setIntersectingCard(outlineCard);
              found = true;
            }
          }
        }

        if (!found) {
          setIntersectingCard(null);
        }
      }
    },
    [cards, outlineCards]
  );

  /**
   * On dragging, calculate intersections with outline cards, and set state accordingly
   */
  const onDragging = useCallback(
    ({ id, deltaX, deltaY }) => {
      if (intersectingCard) {
        // if already intersecting, then recalculate intersection in realtime
        calculateIntersection(id);
      } else {
        // else delay the calculation by a few seconds

        if (!prevDeltas.current) {
          prevDeltas.current = {};
        } else if (prevDeltas.current.x !== deltaX || prevDeltas.current.y !== deltaY) {

          // changed position, so we reset
          if (timeoutId.current) {
            clearTimeout(timeoutId.current);
            timeoutId.current = setTimeout(() => {
              // console.log("here!!");
              calculateIntersection(id);
            }, 1000);
          }
        } else {
          if (!timeoutId.current) {
            console.log("start timeout");
            timeoutId.current = setTimeout(() => {
              // console.log("here!!");
              calculateIntersection(id);
            }, 1000);
          }
        }

        prevDeltas.current.x = deltaX;
        prevDeltas.current.y = deltaY;
      }


    },
    [cards, outlineCards]
  );

  /**
   * Sets droppingOn state based on intersection of cards
   */
  useEffect(() => {
    if (intersectingCard) {
      outlineCards.forEach((oc) => {
        dispatch({ type: SET_DATA_SILENT_ACTION, id: oc.id, data: { droppingOn: intersectingCard.id === oc.id } });
      });
    } else {
      outlineCards.forEach((oc) => {
        dispatch({ type: SET_DATA_SILENT_ACTION, id: oc.id, data: { droppingOn: false } });
      });
    }
  }, [intersectingCard]);

  return {
    onDragEnd, // pass to card triggered when dragging is stopped
    onDragging, // pass to card triggered on every drag movement
  };
};
